import axios from 'axios';

/**
 * API 基础地址优先级：
 * 1. window.__CRM_ENV__.API_BASE_URL  — 运行时配置（public/env.js），部署后可随时修改，无需重新构建
 * 2. process.env.REACT_APP_API_BASE_URL — 构建时 .env 变量
 * 3. '/api' — 默认相对路径（开发时走 devServer proxy，生产时前后端同域）
 */
export function getApiBaseURL() {
  const runtimeBase = window.__CRM_ENV__?.API_BASE_URL;
  if (runtimeBase) return runtimeBase;

  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase) return envBase;

  return '/api';
}

const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
});

// 请求拦截：自动带 token + refresh token 逻辑
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

api.interceptors.request.use((config) => {
  if (typeof localStorage !== 'undefined') {
    const token = localStorage.getItem('crm_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截：401 自动刷新 token
api.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    const originalRequest = err.config;

    // 如果是 401 且不是 refresh 请求本身，尝试刷新
    if (err.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/auth/refresh') {
      const refreshToken = typeof localStorage !== 'undefined' ? localStorage.getItem('crm_refresh_token') : null;

      if (!refreshToken) {
        // 没有 refresh token，跳登录
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('crm_token');
          localStorage.removeItem('crm_refresh_token');
          localStorage.removeItem('crm_user');
        }
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = '/login';
        }
        return Promise.reject(err.response?.data || err);
      }

      if (isRefreshing) {
        // 等待其他请求完成刷新
        return new Promise(resolve => {
          refreshSubscribers.push((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refreshToken });
        const newToken = res.data.token;

        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('crm_token', newToken);
          localStorage.setItem('crm_refresh_token', res.data.refreshToken || refreshToken);
        }

        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // 刷新失败，清空登录状态
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('crm_token');
          localStorage.removeItem('crm_refresh_token');
          localStorage.removeItem('crm_user');
        }
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError.response?.data || refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err.response?.data || err);
  }
);

// === 认证 ===
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  current: () => api.get('/auth/current'),
};

// === 客户 ===
export const customerApi = {
  list: (params) => api.get('/customers', { params }),
  stats: () => api.get('/customers/stats'),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// === 跟进记录 ===
export const logApi = {
  list: (params) => api.get('/logs', { params }),
  create: (data) => api.post('/logs', data),
  delete: (id) => api.delete(`/logs/${id}`),
};

// === 用户管理 ===
export const userApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  delete: (id) => api.delete(`/users/${id}`),
};

// === 微信小程序 ===
export const wxApi = {
  getQrCode: () => api.get('/wx/qrcode'),
};

// ============================================================
// @AGENT_A: 产品 + 仓库 + 采购 API
// ============================================================
export const productApi = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  uploadImage: (formData) => api.post('/products/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const warehouseApi = {
  inventory: (params) => api.get('/warehouse/inventory', { params }),
  stockIns: (params) => api.get('/warehouse/stock-ins', { params }),
  createStockIn: (data) => api.post('/warehouse/stock-ins', data),
  deleteStockIn: (id) => api.delete(`/warehouse/stock-ins/${id}`),
  stockOuts: (params) => api.get('/warehouse/stock-outs', { params }),
  getStockOut: (id) => api.get(`/warehouse/stock-outs/${id}`),
  createStockOut: (data) => api.post('/warehouse/stock-outs', data),
  shipStockOut: (id) => api.put(`/warehouse/stock-outs/${id}/ship`),
  receiveStockOut: (id) => api.put(`/warehouse/stock-outs/${id}/receive`),
  adjustInventory: (id, data) => api.put(`/warehouse/inventory/${id}/adjust`, data),
};

export const supplierApi = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

export const purchaseApi = {
  list: (params) => api.get('/purchases', { params }),
  get: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
  submit: (id) => api.post(`/purchases/${id}/submit`),
  order: (id) => api.post(`/purchases/${id}/order`),
  arrive: (id) => api.post(`/purchases/${id}/arrive`),
};

// ============================================================
// @AGENT_B: 报价 + 合同 API
// ============================================================
export const quotationApi = {
  list: (params) => api.get('/quotations', { params }),
  get: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  send: (id) => api.post(`/quotations/${id}/send`),
  confirm: (id) => api.post(`/quotations/${id}/confirm`),
  convert: (id) => api.post(`/quotations/${id}/convert`),
  cancel: (id) => api.post(`/quotations/${id}/cancel`),
  stats: (params) => api.get('/quotations/stats', { params }),
};

export const contractApi = {
  list: (params) => api.get('/contracts', { params }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  submit: (id) => api.post(`/contracts/${id}/submit`),
  sign: (id) => api.post(`/contracts/${id}/sign`),
  terminate: (id) => api.post(`/contracts/${id}/terminate`),
  payments: (id) => api.get(`/contracts/${id}/payments`),
  addPayment: (id, data) => api.post(`/contracts/${id}/payments`, data),
  deletePayment: (cid, pid) => api.delete(`/contracts/${cid}/payments/${pid}`),
};

// ============================================================
// @AGENT_C: 工作流 API
// ============================================================
export const workflowApi = {
  templates: () => api.get('/workflows/templates'),
  getTemplate: (code) => api.get(`/workflows/templates/${code}`),
  updateTemplate: (code, data) => api.put(`/workflows/templates/${code}`, data),
  createInstance: (data) => api.post('/workflows/instances', data),
  getInstance: (id) => api.get(`/workflows/instances/${id}`),
  approve: (id, data) => api.post(`/workflows/instances/${id}/approve`, data),
  reject: (id, data) => api.post(`/workflows/instances/${id}/reject`, data),
  complete: (id, data) => api.post(`/workflows/instances/${id}/complete`, data),
};

// ============================================================
// @AGENT_D: 企业微信 + 通知 API
// ============================================================
export const wecomApi = {
  authUrl: () => api.get('/auth/wecom/url'),
  callback: (data) => api.get('/auth/wecom/callback', { params: data }),
  getContactQRCode: () => api.get('/wecom/contact-qrcode'),
  syncExternalContacts: () => api.post('/wecom/sync-external-contacts'),
};

export const notificationApi = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
};

// ============================================================
// @AGENT_E: 项目管理 API (P1)
// ============================================================
export const projectApi = {
  list: (params) => api.get('/projects', { params }),
  stats: () => api.get('/projects/stats'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  addLog: (projectId, data) => api.post(`/projects/${projectId}/logs`, data),
  deleteLog: (projectId, logId) => api.delete(`/projects/${projectId}/logs/${logId}`),
};

// ============================================================
// @AGENT_D: 技术文档 + 技术专家 API
// ============================================================
export const documentApi = {
  list: (params) => api.get('/documents', { params }),
  get: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  search: (params) => api.get('/documents/search', { params }),
};

export const expertApi = {
  ask: (data) => api.post('/expert/ask', data),
  troubleshoot: (data) => api.post('/expert/troubleshoot', data),
  recommend: (data) => api.post('/expert/recommend', data),
};

export default api;
